import React, { useState, useCallback } from 'react';
import { useData } from '../context/DataContext';
import { TimeEntry, TimeSheet } from '../types/api.types';
import { BadRequestError } from '../services/apiService';
import DateFilter, { DateField, Comparator } from '../components/DateFilter';
import TimeSheetCard from '../components/TimeSheetCard';
import TimeSheetFilterSort from '../components/TimeSheetFilterSort';
import TimeEntryModal from '../components/TimeEntryModal';
import './Home.css';

const Home: React.FC = () => {
  const { workRoles, workTypes, chargeCodes, cwUsersByMemberId, departmentsDict, reportsToDict, userTypesDict, workRoleValidations, isLoading, error, fetchTimeSheets, uploadWorkRoleValidations } = useData();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadValidations = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      await uploadWorkRoleValidations(file);
    } catch (err) {
      console.error('Failed to upload validations:', err);
      if (err instanceof BadRequestError) {
        alert(err.message);
      } else {
        alert('Failed to upload validations file');
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [uploadWorkRoleValidations]);

  // Date filter state
  const [filterDate, setFilterDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [filterField, setFilterField] = useState<DateField>('deadline');
  const [filterComparator, setFilterComparator] = useState<Comparator>('=');

  // Time sheets state
  const [timeSheets, setTimeSheets] = useState<TimeSheet[]>([]);
  const [displayedSheets, setDisplayedSheets] = useState<TimeSheet[]>([]);
  const [isLoadingSheets, setIsLoadingSheets] = useState(false);
  const [sheetsLoaded, setSheetsLoaded] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMemberIdentifier, setModalMemberIdentifier] = useState('');
  const [modalTimeSheetId, setModalTimeSheetId] = useState<number | null>(null);
  const [copyFromEntry, setCopyFromEntry] = useState<TimeEntry | null>(null);

  // Expand / collapse all signal
  const [expandSignal, setExpandSignal] = useState<{ action: 'expand' | 'collapse'; epoch: number } | undefined>(undefined);
  const handleExpandAll = useCallback(() => {
    setExpandSignal((prev) => ({ action: 'expand', epoch: (prev?.epoch ?? 0) + 1 }));
  }, []);
  const handleCollapseAll = useCallback(() => {
    setExpandSignal((prev) => ({ action: 'collapse', epoch: (prev?.epoch ?? 0) + 1 }));
  }, []);

  // Toggle all time detail signal
  const [timeDetailSignal, setTimeDetailSignal] = useState<{ action: 'show' | 'hide'; epoch: number } | undefined>(undefined);
  const [timeDetailShown, setTimeDetailShown] = useState(false);
  const handleToggleAllTimeDetail = useCallback(() => {
    setTimeDetailShown((prev) => {
      const next = !prev;
      setTimeDetailSignal((s) => ({ action: next ? 'show' : 'hide', epoch: (s?.epoch ?? 0) + 1 }));
      return next;
    });
  }, []);

  // Build conditions string from filter
  const buildConditions = useCallback(() => {
    if (!filterDate) return undefined;
    return `${filterField} ${filterComparator} [${filterDate}]`;
  }, [filterDate, filterField, filterComparator]);

  // Load time sheets with filter
  const handleLoadTimeSheets = useCallback(async () => {
    setIsLoadingSheets(true);

    try {
      const conditions = buildConditions();
      const sheets = await fetchTimeSheets(conditions);

      // DEBUG: log time sheet member ids vs loaded users
      const memberIds = sheets.map(s => s.member?.id).filter(Boolean);
      const unmatchedIds = memberIds.filter(id => !cwUsersByMemberId[id!]);
      console.log('[Home] Time sheet member ids:', memberIds);
      console.log('[Home] Unmatched member ids (not in cwUsersByMemberId):', unmatchedIds);
      if (sheets.length > 0) {
        console.log('[Home] Sample time sheet member:', sheets[0].member);
      }
      // DEBUG: show reportsTo mapping for every time sheet member
      const mgrMap: Record<string, string[]> = {};
      const noMgr: string[] = [];
      sheets.forEach(s => {
        const user = s.member?.id !== undefined ? cwUsersByMemberId[s.member.id] : undefined;
        const mgrId = user?.reportsTo?.id;
        const memberLabel = `${s.member?.name}(${s.member?.id})`;
        if (mgrId !== undefined) {
          const mgrLabel = `${user?.reportsTo?.name || '?'}(${mgrId})`;
          if (!mgrMap[mgrLabel]) mgrMap[mgrLabel] = [];
          mgrMap[mgrLabel].push(memberLabel);
        } else {
          noMgr.push(memberLabel);
        }
      });
      console.log('[Home] Members grouped by reportsTo:', mgrMap);
      console.log('[Home] Members with NO reportsTo:', noMgr);

      // DEBUG: target ebade specifically
      const ebadeSheet = sheets.find(s => s.member?.identifier === 'ebade');
      const ebadeUser = ebadeSheet?.member?.id !== undefined ? cwUsersByMemberId[ebadeSheet.member.id] : undefined;
      console.log('[Home] ebade time sheet member:', ebadeSheet?.member);
      console.log('[Home] ebade CwUser from dict:', ebadeUser);
      console.log('[Home] ebade reportsTo:', ebadeUser?.reportsTo);
      // Also find ebade by scanning all users
      const ebadeByIdent = Object.values(cwUsersByMemberId).find(u => u.identifier === 'ebade');
      console.log('[Home] ebade found by identifier scan:', ebadeByIdent);
      console.log('[Home] ebade found by scan - reportsTo:', ebadeByIdent?.reportsTo);
      // Check againes
      const againesUser = Object.values(cwUsersByMemberId).find(u => u.identifier === 'againes');
      console.log('[Home] againes user:', againesUser);
      console.log('[Home] againes user id:', againesUser?.id);

      setTimeSheets(sheets);
      setSheetsLoaded(true);
    } catch (err) {
      console.error('Error loading time sheets:', err);
    } finally {
      setIsLoadingSheets(false);
    }
  }, [buildConditions, fetchTimeSheets]);

  // Open modal for adding new entry
  const handleAddEntry = useCallback((memberIdentifier: string, timeSheetId: number) => {
    setModalMemberIdentifier(memberIdentifier);
    setModalTimeSheetId(timeSheetId);
    setCopyFromEntry(null);
    setIsModalOpen(true);
  }, []);

  // Open modal for copying from entry
  const handleCopyFrom = useCallback((entry: TimeEntry, timeSheetId: number) => {
    setModalMemberIdentifier(entry.member?.identifier || '');
    setModalTimeSheetId(timeSheetId);
    setCopyFromEntry(entry);
    setIsModalOpen(true);
  }, []);

  // Handle modal save - refresh the specific time sheet
  const handleModalSave = useCallback(async () => {
    if (modalTimeSheetId) {
      // Trigger refresh on the specific TimeSheetCard
      const refreshFn = (window as any)[`refreshTimeSheet_${modalTimeSheetId}`];
      if (refreshFn) {
        await refreshFn();
      }
    }
  }, [modalTimeSheetId]);

  if (isLoading) {
    return (
      <div className="home">
        <div className="loading-state">
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home">
        <div className="error-state">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {isUploading && (
        <div className="loading-overlay">
          <div className="loading-overlay-content">
            <p>Uploading validations…</p>
          </div>
        </div>
      )}
      <div className="page-header">
        <h1 className="page-title">Time Sheets</h1>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          style={{ display: 'none' }}
          onChange={handleUploadValidations}
        />
        <button
          className="btn btn-upload"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          title="Upload an .xlsx file to update work type validations"
        >
          {isUploading ? 'Uploading...' : '📤 Upload Validations'}
        </button>
      </div>

      <div className="filter-section">
        <DateFilter
          date={filterDate}
          field={filterField}
          comparator={filterComparator}
          onDateChange={setFilterDate}
          onFieldChange={setFilterField}
          onComparatorChange={setFilterComparator}
        />
        <button
          className="btn btn-primary load-btn"
          onClick={handleLoadTimeSheets}
          disabled={isLoadingSheets || !filterDate}
        >
          {isLoadingSheets ? 'Loading...' : 'Load Time Sheets'}
        </button>
      </div>

      {sheetsLoaded && timeSheets.length === 0 && (
        <div className="empty-state">
          <p>No time sheets found matching the filter criteria.</p>
        </div>
      )}

      {sheetsLoaded && timeSheets.length > 0 && (
        <>
          <TimeSheetFilterSort
            timeSheets={timeSheets}
            cwUsersByMemberId={cwUsersByMemberId}
            departmentsDict={departmentsDict}
            reportsToDict={reportsToDict}
            userTypesDict={userTypesDict}
            onChange={setDisplayedSheets}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            onToggleAllTimeDetail={handleToggleAllTimeDetail}
            timeDetailShown={timeDetailShown}
          />

          {displayedSheets.length === 0 ? (
            <div className="empty-state">
              <p>No time sheets match the current filters.</p>
            </div>
          ) : (
            <div className="timesheets-list">
              {displayedSheets.map((sheet) => (
                <TimeSheetCard
                  key={sheet.id}
                  sheet={sheet}
                  workRoles={workRoles}
                  workTypes={workTypes}
                  chargeCodes={chargeCodes}
                  workRoleValidations={workRoleValidations}
                  onAddEntry={handleAddEntry}
                  onCopyFrom={handleCopyFrom}
                  expandSignal={expandSignal}
                  timeDetailSignal={timeDetailSignal}
                />
              ))}
            </div>
          )}
        </>
      )}

      {modalTimeSheetId && (
        <TimeEntryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleModalSave}
          memberIdentifier={modalMemberIdentifier}
          timeSheetId={modalTimeSheetId}
          workRoles={workRoles}
          workTypes={workTypes}
          chargeCodes={chargeCodes}
          copyFromEntry={copyFromEntry}
        />
      )}
    </div>
  );
};

export default Home;
